import * as Y from 'yjs';
import {
  keysBetween,
  newId,
  NodeSchema,
  nodeToYMap,
  pageNodes,
  ROOT_ID,
  type Node,
  type Paint,
  type Rect,
} from '@flobi/artwork-core';

export interface StressScene {
  readonly page: Y.Doc;
  readonly bounds: Rect;
  /** Ids of the drawn objects (not artboards), for edit tests. */
  readonly objectIds: readonly string[];
  readonly artboardIds: readonly string[];
}

const BOARD = 1080;
const GAP = 120;

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hsl(h: number, s: number, l: number) {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return { r: f(0), g: f(8), b: f(4), a: 1 };
}

const template = (input: Record<string, unknown>) => NodeSchema.parse({ parentId: ROOT_ID, order: 'a0', ...input });

/**
 * A synthetic file shaped like real work: artboards of social posts, each holding shapes
 * with solid and gradient fills, some strokes and a few shadows. Written straight into
 * Yjs, skipping edit validation, so a 100,000-object page builds in a couple of seconds.
 */
export function buildStressPage(count: number, seed = 42): StressScene {
  const random = mulberry32(seed);
  const boards = Math.max(1, Math.min(100, Math.round(count / 1000)));
  const cols = Math.ceil(Math.sqrt(boards));
  const perBoard = Math.ceil(count / boards);
  const page = new Y.Doc();
  const nodes = pageNodes(page);
  const templates = {
    artboard: template({ type: 'artboard', width: BOARD, height: BOARD }),
    rect: template({ type: 'rect' }),
    ellipse: template({ type: 'ellipse' }),
    star: template({ type: 'star' }),
    path: template({ type: 'path', path: { subpaths: [] } }),
  };
  const defaultStroke = (templates.rect as Extract<Node, { type: 'rect' }>).strokeStyle;
  const objectIds: string[] = [];
  const artboardIds: string[] = [];
  const boardOrders = keysBetween(null, null, boards);

  page.transact(() => {
    let made = 0;
    for (let b = 0; b < boards; b++) {
      const boardId = newId();
      artboardIds.push(boardId);
      const bx = (b % cols) * (BOARD + GAP);
      const by = Math.floor(b / cols) * (BOARD + GAP);
      nodes.set(
        boardId,
        nodeToYMap({ ...templates.artboard, name: `Post ${b + 1}`, order: boardOrders[b]!, transform: [1, 0, 0, 1, bx, by] }),
      );
      const n = Math.min(perBoard, count - made);
      const orders = keysBetween(null, null, n);
      for (let i = 0; i < n; i++) {
        const id = newId();
        objectIds.push(id);
        const size = 20 + random() * 60;
        const x = random() * (BOARD - size);
        const y = random() * (BOARD - size);
        const color = hsl(random() * 360, 0.7, 0.55);
        const fills: Paint[] =
          random() < 0.1
            ? [
                {
                  type: 'linear',
                  stops: [
                    { offset: 0, color },
                    { offset: 1, color: hsl(random() * 360, 0.8, 0.5) },
                  ],
                  start: { x: 0, y: 0 },
                  end: { x: 1, y: 1 },
                  visible: true,
                  opacity: 1,
                  blendMode: 'normal',
                },
              ]
            : [{ type: 'solid', color, visible: true, opacity: 1, blendMode: 'normal' }];
        const stroked = random() < 0.2;
        const shadowed = random() < 0.01;
        const kind = random();
        const base = {
          parentId: boardId,
          order: orders[i]!,
          transform: [1, 0, 0, 1, x, y] as [number, number, number, number, number, number],
          fills,
          strokes: stroked ? ([{ type: 'solid', color: { r: 0.13, g: 0.13, b: 0.16, a: 1 }, visible: true, opacity: 1, blendMode: 'normal' }] as Paint[]) : [],
          strokeStyle: { ...defaultStroke, weight: 1 + Math.floor(random() * 3) },
          effects: shadowed
            ? [{ type: 'drop-shadow' as const, x: 0, y: 4, blur: 12, spread: 0, color: { r: 0, g: 0, b: 0, a: 0.3 }, visible: true, blendMode: 'normal' as const }]
            : [],
        };
        let node: Node;
        if (kind < 0.4) node = { ...templates.rect, ...base, width: size, height: size, radii: [size / 4, size / 4, size / 4, size / 4] } as Node;
        else if (kind < 0.65) node = { ...templates.ellipse, ...base, width: size, height: size } as Node;
        else if (kind < 0.85) node = { ...templates.star, ...base, width: size, height: size, points: 5 } as Node;
        else node = { ...templates.path, ...base, path: blob(size, random) } as Node;
        nodes.set(id, nodeToYMap(node));
        made++;
      }
    }
  });

  const rows = Math.ceil(boards / cols);
  return {
    page,
    bounds: { x: 0, y: 0, width: cols * (BOARD + GAP) - GAP, height: rows * (BOARD + GAP) - GAP },
    objectIds,
    artboardIds,
  };
}

/** A smooth closed shape through eight points at random distances from the centre. */
function blob(size: number, random: () => number) {
  const r = size / 2;
  const count = 8;
  const points = Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2;
    const radius = r * (0.6 + random() * 0.4);
    const x = r + Math.cos(angle) * radius;
    const y = r + Math.sin(angle) * radius;
    const handle = radius * 0.39;
    const tx = -Math.sin(angle) * handle;
    const ty = Math.cos(angle) * handle;
    return { x, y, in: [x - tx, y - ty] as [number, number], out: [x + tx, y + ty] as [number, number], kind: 'smooth' as const };
  });
  return { fillRule: 'nonzero' as const, subpaths: [{ closed: true, points }] };
}
